import { useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "../../lib/api";

export const TextField = ({ label, value, onChange, testid, placeholder = "" }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-jauja-olive focus:ring-1 focus:ring-jauja-olive"
    />
  </label>
);

export const TextArea = ({ label, value, onChange, testid, rows = 4, placeholder = "" }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testid}
      placeholder={placeholder}
      className="mt-1.5 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none transition-colors focus:border-jauja-olive focus:ring-1 focus:ring-jauja-olive resize-y"
    />
  </label>
);

export const ImageField = ({ label, value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100">
          {value ? (
            <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-zinc-300 text-xs">Sin imagen</div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid ? `${testid}-url` : undefined}
            placeholder="URL de imagen o sube un archivo"
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-jauja-olive"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={testid ? `${testid}-upload` : undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-medium text-white hover:bg-zinc-700 transition-colors disabled:opacity-60"
            disabled={uploading}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Subir imagen
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        </div>
      </div>
    </div>
  );
};

export const SectionCard = ({ title, description, children }) => (
  <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
    <h3 className="font-display text-xl text-zinc-900">{title}</h3>
    {description && <p className="mt-1 text-sm text-zinc-500">{description}</p>}
    <div className="mt-5 space-y-5">{children}</div>
  </div>
);

export const RemoveBtn = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50">
    <X size={14} /> Eliminar
  </button>
);
